import { isNullable } from "../../type-guards";
export class ObjectUtils {
    static isObject(v) {
        return (typeof v === "object" &&
            !Array.isArray(v) &&
            !(v instanceof Date) &&
            v !== null);
    }
    static set(data, key, value) {
        const [key1, key2] = key.split(".");
        return {
            ...data,
            [key1]: {
                ...(data[key1] || {}),
                [key2]: value,
            },
        };
    }
    static setValue(key, target, value) {
        const [key1, ...keys] = key.split(".");
        return {
            ...(target ?? {}),
            [key1]: keys.length > 0
                ? this.setValue(keys.join("."), target[key1] ?? {}, value)
                : value,
        };
    }
    static getValue(key, target) {
        const [key1, ...keys] = key.split(".");
        if (!this.isObject(target)) {
            return undefined;
        }
        const value = target[key1];
        return keys.length === 0 ? value : this.getValue(keys.join("."), value);
    }
    // Object.entriesはkeyの型が推論されない。keyとValueの型の組み合わせ情報を保持したままentriesを取得したいときに使う。
    // ref: https://zenn.dev/8times12/articles/ff08c1fac412c9
    static entries(obj) {
        return Object.entries(obj);
    }
    /**
     * Date型などのプロパティはコピーできないので注意
     * @param v copy target
     * @returns
     */
    static deepClone(v) {
        return JSON.parse(JSON.stringify(v));
    }
    static keys(v) {
        return Object.keys(v);
    }
    static flatternKeys(v) {
        return Object.keys(v).reduce((acc, key) => {
            const value = v[key]; // Add index signature to access the object property
            if (this.isObject(value)) {
                return [...acc, ...this.flatternKeys(value).map((k) => `${key}.${k}`)];
            }
            return [...acc, key];
        }, []);
    }
    // valueがJSON.stringifyで比較できる型の場合のみ使える
    static equals(a, b) {
        if (a === b) {
            return true;
        }
        // keysで比較できない系
        if (Object.keys(a).length === 0 || Object.keys(b).length === 0) {
            return JSON.stringify(a) === JSON.stringify(b);
        }
        // keysで比較できる場合はkeyごとに一つずつ確認
        if (Object.keys(a).sort().join(",") !== Object.keys(b).sort().join(",")) {
            return false;
        }
        return Object.entries(a).every(([key, aValue]) => {
            const bValue = b[key];
            return this.equalsInternal(aValue, bValue);
        });
    }
    static equalsInternal(a, b) {
        if (a === b) {
            return true;
        }
        if (typeof a === "object" && typeof b === "object") {
            return this.equals(a, b);
        }
        return JSON.stringify(a) === JSON.stringify(b);
    }
    static sorted(v) {
        if (!this.isObject(v)) {
            return v;
        }
        const sortedList = Object.entries(v).sort();
        const sortedObject = {};
        sortedList.forEach(([key, value]) => {
            sortedObject[key] = this.sorted(value);
        });
        return sortedObject;
    }
    static deepSorted(v) {
        if (Array.isArray(v)) {
            const normalizedArray = v.map((item) => this.deepSorted(item));
            if (normalizedArray.every((e) => isNullable(e) || ["string", "number", "boolean"].includes(typeof e))) {
                return normalizedArray.sort();
            }
            return normalizedArray;
        }
        if (this.isObject(v)) {
            const sortedEntries = Object.entries(v).sort();
            const result = {};
            for (const [key, value] of sortedEntries) {
                result[key] = this.deepSorted(value);
            }
            return result;
        }
        return v;
    }
    static isEmpty(target) {
        return Object.keys(target).length === 0;
    }
    static deleteKeyIfNull(target) {
        const newTarget = { ...target };
        ObjectUtils.keys(newTarget).forEach((key) => {
            if (isNullable(newTarget[key])) {
                delete newTarget[key];
            }
        });
        return newTarget;
    }
    static deepDeleteKeyIfNull(target) {
        const newTarget = { ...target };
        ObjectUtils.keys(newTarget).forEach((key) => {
            if (this.isObject(newTarget[key])) {
                newTarget[key] = this.deepDeleteKeyIfNull(newTarget[key]);
            }
            else if (Array.isArray(newTarget[key])) {
                newTarget[key] = newTarget[key].map((v) => {
                    if (this.isObject(v)) {
                        return this.deepDeleteKeyIfNull(v);
                    }
                    return v;
                });
            }
            else if (isNullable(newTarget[key])) {
                delete newTarget[key];
            }
        });
        return newTarget;
    }
}
//# sourceMappingURL=object-utils.js.map